const { ethers, FixedNumber } = require("ethers");
const { preparePrice, calculateScaleAdjustment, calculateFormatedPrice } = require("../../utils/bond-math");

// Calculate market price from payout and quote prices with discount,
// `payoutPrice` and `quotePrice` should be in the same units (e.g. USD).
//
// * payoutPrice - The price of the payout token
// * quotePrice - The price of the quote token
// * discount - The discount in percents
function calculateMarketPrice(payoutPrice, quotePrice, discount = 0) {
  if (discount < 0 || discount > 100) {
    throw new Error("Discount should be between 0 and 100");
  }

  // Apply discount
  payoutPrice = (payoutPrice * (100 - discount)) / 100;

  return quotePrice / payoutPrice;
}

// Calculate market parameters from payout and quote prices with discount,
// `payoutPrice` and `quotePrice` should be in the same units (e.g. USD).
//
// * payoutDecimals - Number of decimals defined for the payout token in its ERC20 contract
// * payoutPrice - The price of the payout token
// * quoteDecimals - Number of decimals defined for the quote token in its ERC20 contract
// * quotePrice - The price of the quote token
// * discount - The discount in percents
function calculateFPAMarketParameters(payoutDecimals, payoutPrice, quoteDecimals, quotePrice, discount = 0) {
  if (discount < 0 || discount > 100) {
    throw new Error("Discount should be between 0 and 100");
  }

  // Apply discount
  payoutPrice = (payoutPrice * (100 - discount)) / 100;

  const { priceCoefficient: payoutPriceCoefficient, priceDecimals: payoutPriceDecimals } = preparePrice(payoutPrice);
  const { priceCoefficient: quotePriceCoefficient, priceDecimals: quotePriceDecimals } = preparePrice(quotePrice);

  const scaleAdjustment = calculateScaleAdjustment(
    ethers.BigNumber.from(payoutDecimals),
    payoutPriceDecimals,
    ethers.BigNumber.from(quoteDecimals),
    quotePriceDecimals
  );

  const formattedPrice = calculateFormatedPrice(
    ethers.BigNumber.from(payoutDecimals),
    payoutPriceCoefficient,
    payoutPriceDecimals,
    ethers.BigNumber.from(quoteDecimals),
    quotePriceCoefficient,
    quotePriceDecimals,
    scaleAdjustment
  );

  return {
    formattedPrice,
    scaleAdjustment,
  };
}

function calculateSDAMarketParameters(
  payoutDecimals,
  payoutInitialPrice,
  payoutMinimumPrice,
  quoteDecimals,
  quotePrice
) {
  let { priceCoefficient: payoutInitialPriceCoefficient, priceDecimals: payoutInitialPriceDecimals } =
    preparePrice(payoutInitialPrice);
  let { priceCoefficient: payoutMinimumPriceCoefficient, priceDecimals: payoutMinimumPriceDecimals } =
    preparePrice(payoutMinimumPrice);
  const { priceCoefficient: quotePriceCoefficient, priceDecimals: quotePriceDecimals } = preparePrice(quotePrice);

  if (payoutInitialPriceDecimals.lt(payoutMinimumPriceDecimals)) {
    throw new Error("Payout minimum price decimals should be less or equal to payout initial price decimals");
  } else {
    // Should convert price decimals to same value
    // so we can use the same scale adjustment for both prices
    const difference = payoutInitialPriceDecimals - payoutMinimumPriceDecimals;
    payoutMinimumPriceCoefficient = FixedNumber.from(`${payoutMinimumPriceCoefficient / 10 ** difference}`);
    payoutMinimumPriceDecimals = payoutInitialPriceDecimals;
  }

  const scaleAdjustment = calculateScaleAdjustment(
    ethers.BigNumber.from(payoutDecimals),
    payoutInitialPriceDecimals,
    ethers.BigNumber.from(quoteDecimals),
    quotePriceDecimals
  );

  const formattedInitialPrice = calculateFormatedPrice(
    ethers.BigNumber.from(payoutDecimals),
    payoutInitialPriceCoefficient,
    payoutInitialPriceDecimals,
    ethers.BigNumber.from(quoteDecimals),
    quotePriceCoefficient,
    quotePriceDecimals,
    scaleAdjustment
  );

  const formattedMinimumPrice = calculateFormatedPrice(
    ethers.BigNumber.from(payoutDecimals),
    payoutMinimumPriceCoefficient,
    payoutMinimumPriceDecimals,
    ethers.BigNumber.from(quoteDecimals),
    quotePriceCoefficient,
    quotePriceDecimals,
    scaleAdjustment
  );

  return {
    formattedInitialPrice,
    formattedMinimumPrice,
    scaleAdjustment,
  };
}

const BondMath = {
  calculateMarketPrice,
  calculateFPAMarketParameters,
  calculateSDAMarketParameters,
};

module.exports = { BondMath };
